import { useQuery } from '@tanstack/react-query';

import { api, ApiError } from '@shared/api/client';
import { key, STALE_TIME } from '@shared/api/query-keys';

import {
  BYTES_PER_MB,
  CREDIT_ROLES_FALLBACK,
  CREDIT_ROLES_KEY,
  MAX_PAGE_SIZE,
  PORTRAIT_PURPOSE,
  PORTRAIT_SUBJECT_TYPE,
} from './constants';
import type {
  AdminContentItemRow,
  ConfigurationRow,
  CreditedItemRow,
  CreditProfileRecord,
  PagedResult,
} from './types';

/**
 * Every read the two credited-person surfaces make.
 *
 * Three of them are compositions rather than a single call, and each one is
 * here because the route that would express it directly does not exist yet.
 * They are written as the **narrowest** thing the published routes can say, and
 * every ceiling they inherit is stated where an operator can see its effect.
 */

/** The exact key for one term's page. */
export function creditProfilesKey(term = '') {
  return key('content', 'creditProfiles', { term });
}

/**
 * The prefix every term's page shares, for invalidation.
 *
 * A write changes the register whichever term is on screen, and react-query
 * matches a `queryKey` by prefix — so invalidating the scope retires the cached
 * page for every term rather than only the empty one.
 */
export function creditProfilesScope() {
  return key('content', 'creditProfiles');
}

export function creditProfileKey(slug: string) {
  return key('content', 'creditProfile', slug);
}

export function creditedItemsKey(profileId: string) {
  return key('content', 'creditedItems', profileId);
}

export function creditRolesKey() {
  return key('content', 'configurations', CREDIT_ROLES_KEY);
}

export function titleOptionsKey(term: string) {
  return key('content', 'adminItems', { for: 'creditPicker', q: term });
}

/**
 * The register, **searched and sized on the service**.
 *
 * `GET /admin/creditProfiles` has always taken `term` and `page_size` — the
 * route signature carries both, and the service runs `term` against
 * `name_normalized` with the same Arabic fold as everything else here. This
 * query sent neither, so the register was the service's default 20 rows of 34
 * with no way to reach the rest: no search, no filter, no sort, no page size.
 * Fourteen people could not be opened, edited or credited from the only surface
 * that maintains them, and the surface's own copy said the service «تسلّم دفعةً
 * واحدة» — which was not true of the route, only of the call.
 *
 * `page_size` is the route's own bound (`Query(ge=1, le=100)`) quoted back, not
 * a number this console chose.
 */
export function useCreditProfiles(term: string) {
  const trimmed = term.trim();

  return useQuery({
    queryKey: creditProfilesKey(trimmed),
    queryFn: () => {
      const query = new URLSearchParams({ page_size: String(MAX_PAGE_SIZE) });
      if (trimmed) query.set('term', trimmed);
      return api.get<CreditProfileRecord[]>('content', `/admin/creditProfiles?${query.toString()}`);
    },
    staleTime: STALE_TIME.list,
    // The previous term's rows stay while the next read is in flight, so the
    // table does not blank out between keystrokes.
    placeholderData: (previous) => previous,
  });
}

/**
 * One credited person, read by **slug**.
 *
 * `GET /api/v1/creditHolders/{profileSlug}` is the same record from the same
 * table through the same service as the operator list — the public page's head
 * read, and the only published route that returns one profile. There is no
 * `GET /admin/creditProfiles/{profileId}`: the service implements
 * `perform_fetch_by_id`, and only its API-key-gated internal surface calls it,
 * which this bundle can neither reach nor hold a credential for.
 *
 * So the detail route keys on the slug — which the pack already calls this
 * record's only identifier — and the **id** the credit writes need comes back
 * inside this payload. Reading a different subject to stand in for this one
 * would have been the substitution this composition exists to avoid.
 */
export function useCreditProfile(slug: string) {
  return useQuery({
    queryKey: creditProfileKey(slug),
    queryFn: () => api.get<CreditProfileRecord>('content', `/creditHolders/${encodeURIComponent(slug)}`),
    // A detail route reflects the decision that was just taken.
    staleTime: STALE_TIME.detail,
    enabled: slug.length > 0,
  });
}

/**
 * Every title this person is credited on — one row per **relation**, so a title
 * carrying two of their credits appears twice.
 *
 * **Read through the OPERATOR route, keyed on the profile id.** This used to
 * call the public `/creditHolders/{slug}/contentItems`, which is correctly
 * narrowed to the published set — so the table on an operator's own maintenance
 * surface could not show the result of that operator's own write. Attaching a
 * credit to a draft title answered `201`, the table stayed as it was through a
 * full reload, and attaching it again answered `409` for a row nothing on screen
 * had ever mentioned. `GET /admin/creditProfiles/{profileId}/credits` returns
 * the same shape over every publish state.
 *
 * `page_size` is the service's own ceiling quoted back rather than a number this
 * console picked: the surface states the completeness of this list out loud in
 * its count line, so it asks for as much of it as the route will serve in one
 * page. Past that ceiling the figures under-report, and the line says so rather
 * than implying a total it did not read.
 *
 * The same query serves the register's role and works columns, so opening a
 * person after seeing their row costs no second read.
 */
export function useCreditedItems(profileId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: creditedItemsKey(profileId),
    queryFn: () =>
      api.get<PagedResult<CreditedItemRow>>(
        'content',
        `/admin/creditProfiles/${encodeURIComponent(profileId)}/credits?page_size=${MAX_PAGE_SIZE}`,
      ),
    staleTime: STALE_TIME.list,
    enabled: (options.enabled ?? true) && profileId.length > 0,
  });
}

/**
 * The role vocabulary — read, then seeded, never emptied.
 *
 * `GET /admin/configurations` on `backend-content` is gated on the
 * configuration permission, which is the `admin` role; this surface is open to
 * an `operator`. An operator therefore meets a `403` here, and that is not a
 * failure to render: the catalog declares the value, so the fallback is the
 * product's own list rather than an empty control.
 *
 * A `403` resolves to the fallback. Anything else is a real failure and is
 * allowed to surface, because a network fault is not a configuration decision.
 */
export function useCreditRoles() {
  return useQuery({
    queryKey: creditRolesKey(),
    queryFn: async (): Promise<readonly string[]> => {
      try {
        const rows = await api.get<ConfigurationRow[]>('content', '/admin/configurations');
        const row = rows.find((candidate) => candidate.key === CREDIT_ROLES_KEY);
        const served = row?.value
          .split(',')
          .map((token) => token.trim())
          .filter(Boolean);

        return served && served.length > 0 ? served : CREDIT_ROLES_FALLBACK;
      } catch (error) {
        if (error instanceof ApiError && error.status === 403) return CREDIT_ROLES_FALLBACK;
        throw error;
      }
    },
    // A vocabulary changes about once a quarter.
    staleTime: STALE_TIME.vocabulary,
  });
}

/**
 * The credit dialog's title options — **searched on the server**.
 *
 * The operator content list is the right subject set: a credit may point at a
 * title in **any** publish state, and public search indexes only what is
 * published — resolving the picker against it would quietly hide every draft.
 *
 * The term used to be filtered client-side over one page, so a title past the
 * ceiling could not be credited at all. `GET /admin/contentItems` takes `q` now
 * and the read carries it, which turns the ceiling from «the only titles that
 * exist for this dialog» into «the size of one answer» — the dialog still says
 * which it is, because a hundred rows is not the whole catalogue either.
 *
 * `page_size` is the route's own bound quoted back, not a number chosen here.
 */
export function useTitleOptions(enabled: boolean, term = '') {
  const trimmed = term.trim();

  return useQuery({
    queryKey: titleOptionsKey(trimmed),
    queryFn: () => {
      const query = new URLSearchParams({
        page_size: String(MAX_PAGE_SIZE),
        current_page: '1',
      });
      if (trimmed) query.set('q', trimmed);
      return api.get<PagedResult<AdminContentItemRow>>('content', `/admin/contentItems?${query.toString()}`);
    },
    staleTime: STALE_TIME.list,
    // The previous term's rows stay on screen while the next read is in flight.
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** The upload contract for a credited person's portrait, as `assets` publishes it. */
export function portraitPolicyKey() {
  return key('assets', 'uploadPolicy', PORTRAIT_SUBJECT_TYPE);
}

interface WirePortraitPolicy {
  acceptedArtworkFormats?: string[];
  maxArtworkUploadMb?: number;
  artwork?: Record<string, { purposes?: Record<string, string>; ratios?: Record<string, string> }>;
  minDimensionsByRatio?: Record<string, number[]>;
}

/** The four numbers the portrait control states, every one of them read. */
export interface PortraitPolicy {
  accepted_formats: string[];
  max_size_bytes: number | null;
  min_width: number | null;
  min_height: number | null;
}

/**
 * Read the policy **narrowed to this subject**, because the route takes the
 * narrowing and the answer differs per subject.
 *
 * Every value the control prints comes from here rather than from a constant
 * beside it: the accepted formats, the cap, and the floor are all operator-owned
 * configuration, and this endpoint exists precisely so none of them is a literal
 * in a frontend. A hard-coded «حتّى ٢٠ ميغابايت» is wrong the day an operator
 * changes the row, and wrong silently.
 */
export function usePortraitPolicy() {
  return useQuery({
    queryKey: portraitPolicyKey(),
    queryFn: async (): Promise<PortraitPolicy> => {
      const wire = await api.get<WirePortraitPolicy>(
        'assets',
        `/uploadPolicy?subjectType=${PORTRAIT_SUBJECT_TYPE}`,
      );
      const ratio = wire.artwork?.[PORTRAIT_SUBJECT_TYPE]?.ratios?.[PORTRAIT_PURPOSE] ?? '';
      const floor = wire.minDimensionsByRatio?.[ratio];

      return {
        accepted_formats: wire.acceptedArtworkFormats ?? [],
        max_size_bytes:
          typeof wire.maxArtworkUploadMb === 'number' ? wire.maxArtworkUploadMb * BYTES_PER_MB : null,
        min_width: floor?.[0] ?? null,
        min_height: floor?.[1] ?? null,
      };
    },
    staleTime: STALE_TIME.vocabulary,
  });
}
